import {useCallback, useEffect, useState} from "react";
import type {FormEvent} from "react";
import {
  createProduct, listProducts, listWorkPackageApprovers,
  transitionProduct, updateProduct,
  type ApproverIdentity, type Product, type ProductDraft
} from "../../api/client";
import {useAuth} from "../../auth/AuthContext";
import {AsyncState} from "../../components/AsyncState";
import {RegistryShell} from "../../components/Registry";
import {StatusBadge} from "../../components/StatusBadge";

const blank: ProductDraft = {key: "", name: "", description: "",
  type: "ENGINEERING_PLATFORM", ownerOrganization: "", namedApprover: ""};

export function ProductRegistry() {
  const {authorizedFetch, user} = useAuth();
  const [items, setItems] = useState<Product[]>([]);
  const [approvers, setApprovers] = useState<ApproverIdentity[]>([]);
  const [draft, setDraft] = useState<ProductDraft>(blank);
  const [editing, setEditing] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mayAuthor = !!user && user.roles.some(role =>
    role === "workbench-operator" || role === "chief-product-architect");
  const mayApprove = !!user && user.roles.some(role =>
    role === "workbench-approver" || role === "governance-approver");
  const refresh = useCallback(async (signal?: AbortSignal) => {
    const [products, humans] = await Promise.all([
      listProducts(authorizedFetch, "", signal),
      listWorkPackageApprovers(authorizedFetch, signal)
    ]);
    setItems(products); setApprovers(humans);
  }, [authorizedFetch]);
  useEffect(() => { const controller = new AbortController();
    void refresh(controller.signal).catch(problem => !controller.signal.aborted && setError(message(problem)));
    return () => controller.abort(); }, [refresh]);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true);
    try {
      const saved = editing
        ? await updateProduct(authorizedFetch, editing, draft)
        : await createProduct(authorizedFetch, draft);
      setItems(current => [saved, ...current.filter(item => item.id !== saved.id)]);
      close(); setError("");
    } catch (problem) { setError(message(problem)); } finally { setBusy(false); }
  };
  const transition = async (product: Product, action: "validate" | "submit" | "activate" | "revise") => {
    const rationale = action === "activate" || action === "revise"
      ? window.prompt(`${action} rationale`)?.trim() ?? "" : "";
    if ((action === "activate" || action === "revise") && !rationale) return;
    setBusy(true);
    try {
      const saved = await transitionProduct(authorizedFetch, product, action, rationale);
      setItems(current => current.map(item => item.id === saved.id ? saved : item)); setError("");
    } catch (problem) { setError(message(problem)); } finally { setBusy(false); }
  };
  const close = () => { setShowForm(false); setEditing(null); setDraft(blank); };
  const edit = (product: Product) => { setEditing(product); setDraft({key: product.key,
    name: product.name, description: product.description, type: product.type,
    ownerOrganization: product.ownerOrganization, namedApprover: product.namedApprover}); setShowForm(true); };

  return <RegistryShell title="Products"
    description="Canonical same-scope Product governance. PLANNED is the mutable Draft lifecycle; validation and submission bind exact immutable versions before ACTIVE approval."
    tools={mayAuthor && <button className="primary-action" type="button" onClick={() => {
      setDraft({...blank, ownerOrganization: user?.company ?? ""}); setShowForm(true);
    }}>Create Product Draft</button>}>
    {error && <p className="form-message error" role="alert">{error}</p>}
    {showForm && <form className="program-draft panel" onSubmit={event => void submit(event)}>
      <header><div><p className="eyebrow">Governed Product</p><h2>{editing ? `Edit ${editing.key}` : "Create Product Draft"}</h2></div><StatusBadge value="PLANNED" /></header>
      <div className="form-grid"><label>Key<input required pattern="[A-Z][A-Z0-9-]{2,99}" value={draft.key} onChange={event => setDraft({...draft,key:event.target.value.toUpperCase()})} /></label>
        <label>Name<input required value={draft.name} onChange={event => setDraft({...draft,name:event.target.value})} /></label>
        <label>Type<select value={draft.type} onChange={event => setDraft({...draft,type:event.target.value as ProductDraft["type"]})}><option>ENGINEERING_PLATFORM</option><option>PLATFORM</option><option>PLATFORM_SERVICE</option><option>BUSINESS_PRODUCT</option></select></label>
        <label>Owner organization<input required value={draft.ownerOrganization} onChange={event => setDraft({...draft,ownerOrganization:event.target.value})} /></label>
        <label>Named approver<select required value={draft.namedApprover} onChange={event => setDraft({...draft,namedApprover:event.target.value})}><option value="">Select different human</option>{approvers.map(item => <option key={item.username} value={item.username}>{item.username} — {item.displayName}</option>)}</select></label>
        <label className="span-2">Description<textarea required minLength={10} rows={4} value={draft.description} onChange={event => setDraft({...draft,description:event.target.value})} /></label></div>
      <div className="form-actions"><button type="button" onClick={close}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? "Saving…" : "Save exact Draft"}</button></div>
    </form>}
    {items.length === 0 ? <AsyncState kind="empty" detail="No governed Product exists in this scope." />
      : <div className="registry-table"><table><thead><tr><th>Product</th><th>Type</th><th>State</th><th>Exact version</th><th>Actions</th></tr></thead><tbody>
        {items.map(product => <tr key={product.id}><td><a href={`/products/${product.id}`}>{product.key}</a><span>{product.name}</span></td><td>{product.type}</td><td><StatusBadge value={state(product)} /></td><td>v{product.version}</td><td className="row-actions">
          {mayAuthor && product.lifecycle === "PLANNED" && product.submittedVersion == null && <button onClick={() => edit(product)}>Edit</button>}
          {mayAuthor && canValidate(product) && <button disabled={busy} onClick={() => void transition(product,"validate")}>Validate</button>}
          {mayAuthor && canSubmit(product) && <button disabled={busy} onClick={() => void transition(product,"submit")}>Submit</button>}
          {mayApprove && product.namedApprover === user?.username && canActivate(product) && <button disabled={busy} onClick={() => void transition(product,"activate")}>Approve & activate</button>}
          {mayAuthor && product.lifecycle === "ACTIVE" && <button disabled={busy} onClick={() => void transition(product,"revise")}>Open revision</button>}
        </td></tr>)}</tbody></table></div>}
  </RegistryShell>;
}

function canValidate(value: Product) { return value.lifecycle === "PLANNED" && value.submittedVersion == null && value.validatedVersion !== value.version; }
function canSubmit(value: Product) { return value.lifecycle === "PLANNED" && value.validatedVersion === value.version && value.submittedVersion == null; }
function canActivate(value: Product) { return value.lifecycle === "PLANNED" && value.submittedVersion === value.version; }
function state(value: Product) { if (canActivate(value)) return "AWAITING_NAMED_HUMAN"; if (canSubmit(value)) return "VALIDATED"; return value.lifecycle; }
function message(problem: unknown) { return problem instanceof Error ? problem.message : "Product operation failed"; }
